import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Combobox, Disclosure, Switch } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Playbook } from '@/types/playbook';
import PlaybookStatsPanel from '@components/playbooks/PlaybookStatsPanel';
import LogIn04 from '@/components/icons/LogIn04';
import LogOut04 from '@/components/icons/LogOut04';
import { fetchAllUnifiedSchemas } from '../../services/schemaDefinitionApiService';
import { SchemaType, UnifiedSchema } from '@/types/schema'; // Import SchemaType
import { useNotifications } from '../../contexts/NotificationContext';
import { Integration } from '@/types/integration'; // Import Integration type
import { integrationService } from '../../services/integration.service'; // Import integrationService
import ConfirmDialog from '../dialogs/ConfirmDialog';
import { getMappedFieldCounts, getSchemaFieldCounts } from '../../utils/playbookSchemaUtils'; // Import new utilities
import { PlaybookParamValue, ConnectionParam } from '@/types/playbook';

interface PlaybookDetailsTabProps {
  state: {
    playbookId: string;
    name: string;
    description: string;
    type: Playbook['type'];
    version: string;
    params: PlaybookParamValue[];
    input_schema_id?: string;
    output_schema_id?: string;
    uiTransforms: any[];
    uiEventConfigs: any[];
    autoPromoteGlobals: boolean;
    allowUndefinedTransforms: boolean; // New setting
    isNewPlaybook: boolean;
    isSubmitting: boolean;
    baselineSchema?: 'input' | 'output';
    connectionJSON: {
      params: ConnectionParam[];
    } | null;
  };
  updateState: (updates: any) => void;
}

const PlaybookDetailsTab: React.FC<PlaybookDetailsTabProps> = ({ state, updateState }) => {
  const { showNotification } = useNotifications();
  const [schemaDefinitions, setSchemaDefinitions] = useState<UnifiedSchema[]>([]);
  const [isLoadingSchemas, setIsLoadingSchemas] = useState(true);
  const [inputSchemaQuery, setInputSchemaQuery] = useState('');
  const [outputSchemaQuery, setOutputSchemaQuery] = useState('');
  const [integrations, setIntegrations] = useState<Integration[]>([]); // New state for integrations
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingBaselineSchema, setPendingBaselineSchema] = useState<'input' | 'output' | null>(null);

  const inputSchemaButtonRef = useRef<HTMLButtonElement>(null);
  const outputSchemaButtonRef = useRef<HTMLButtonElement>(null);
  
  // Load schema definitions and integrations
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoadingSchemas(true);
        const [customerSchemas, vendorSchemas, pipelineSchemas, integrationsResponse] = await Promise.all([
          fetchAllUnifiedSchemas(SchemaType.CustomerSchema),
          fetchAllUnifiedSchemas(SchemaType.VendorSchema),
          fetchAllUnifiedSchemas(SchemaType.PipelineSchema),
          integrationService.getIntegrations(), // Fetch integrations
        ]);
        
        const allSchemas = [...customerSchemas, ...vendorSchemas, ...pipelineSchemas];
        setSchemaDefinitions(allSchemas);
        setIntegrations(integrationsResponse.data); // Set integrations from the 'data' property
      } catch (error: any) {
        showNotification('error', 'Failed to load data');
      } finally {
        setIsLoadingSchemas(false);
      }
    };
    
    loadData();
  }, [showNotification]);

  // Set default schemas and baseline schema based on playbook type
  useEffect(() => {
    if (schemaDefinitions.length > 0) {
      let newInputSchemaId: string | undefined;
      let newOutputSchemaId: string | undefined;

      if (state.type === 'pipeline') {
        newInputSchemaId = schemaDefinitions.find(s => s.codeName === 'customer_schema')?.id;
        newOutputSchemaId = schemaDefinitions.find(s => s.codeName === 'pipeline_schema')?.id;
      } else if (state.type === 'integration') {
        newInputSchemaId = schemaDefinitions.find(s => s.codeName === 'pipeline_schema')?.id;
        newOutputSchemaId = schemaDefinitions.find(s => s.codeName === 'vendor_schema')?.id;
      }

      const updates: any = {};
      if (newInputSchemaId && state.input_schema_id !== newInputSchemaId) {
        updates.input_schema_id = newInputSchemaId;
      }
      if (newOutputSchemaId && state.output_schema_id !== newOutputSchemaId) {
        updates.output_schema_id = newOutputSchemaId;
      }

      // Set default baseline schema if not already set
      if (!state.baselineSchema) {
        if (state.type === 'pipeline') {
          updates.baselineSchema = 'input';
        } else if (state.type === 'integration') {
          updates.baselineSchema = 'output';
        }
      }

      if (Object.keys(updates).length > 0) {
        updateState(updates);
      }
    }
  }, [schemaDefinitions, state.input_schema_id, state.output_schema_id, state.type, state.baselineSchema, updateState]);
  
  // Filter schema definitions based on search query and playbook type
  const filteredInputSchemas = useMemo(() => {
    let schemas = schemaDefinitions;
    if (state.type === 'pipeline') {
      schemas = schemas.filter(s => s.type === 'customer_schema');
    } else if (state.type === 'integration') {
      schemas = schemas.filter(s => s.type === 'pipeline_schema');
    }

    const result = inputSchemaQuery === ''
      ? schemas
      : schemas.filter(schema =>
          schema.name.toLowerCase().includes(inputSchemaQuery.toLowerCase()) ||
          schema.codeName.toLowerCase().includes(inputSchemaQuery.toLowerCase())
        );
    return result;
  }, [inputSchemaQuery, schemaDefinitions, state.type]);

  const filteredOutputSchemas = useMemo(() => {
    let schemas = schemaDefinitions;
    if (state.type === 'pipeline') {
      schemas = schemas.filter(s => s.type === 'pipeline_schema');
    } else if (state.type === 'integration') {
      schemas = schemas.filter(s => s.type === 'vendor_schema');
    }

    const result = outputSchemaQuery === ''
      ? schemas
      : schemas.filter(schema =>
          schema.name.toLowerCase().includes(outputSchemaQuery.toLowerCase()) ||
          schema.codeName.toLowerCase().includes(outputSchemaQuery.toLowerCase())
        );
    return result;
  }, [outputSchemaQuery, schemaDefinitions, state.type]);
  
  // Get selected schemas and their associated integration
  const selectedInputSchema = schemaDefinitions.find(schema => schema.id === state.input_schema_id);
  const selectedOutputSchema = schemaDefinitions.find(schema => schema.id === state.output_schema_id);
  const selectedIntegration = selectedOutputSchema?.integrationId
    ? integrations.find(int => int.id === selectedOutputSchema.integrationId)
    : null;

  // Calculate mapping progress for the baseline schema
  const {
    mappedCount,
    totalCount,
    requiredMappedCount,
    requiredTotalCount,
  } = useMemo(() => {
    let currentSchema: UnifiedSchema | undefined;
    let isInputBaseline = false;

    if (state.baselineSchema === 'input' && selectedInputSchema) {
      currentSchema = selectedInputSchema;
      isInputBaseline = true;
    } else if (state.baselineSchema === 'output' && selectedOutputSchema) {
      currentSchema = selectedOutputSchema;
      isInputBaseline = false;
    }

    if (!currentSchema) {
      return { mappedCount: 0, totalCount: 0, requiredMappedCount: 0, requiredTotalCount: 0 };
    }

    const { totalCount, requiredTotalCount } = getSchemaFieldCounts(currentSchema);
    const { mappedCount, requiredMappedCount } = getMappedFieldCounts(currentSchema, state.uiTransforms, isInputBaseline);

    return { mappedCount, totalCount, requiredMappedCount, requiredTotalCount };
  }, [state.baselineSchema, state.uiTransforms, selectedInputSchema, selectedOutputSchema]);

  // Handle baseline schema change
  const handleBaselineSchemaChange = (newBaselineSchema: 'input' | 'output') => {
    // Check if there are existing events that might be affected
    const hasExistingEvents = state.uiEventConfigs && state.uiEventConfigs.length > 0;
    
    if (hasExistingEvents && state.baselineSchema && state.baselineSchema !== newBaselineSchema) {
      // Show confirmation dialog
      setPendingBaselineSchema(newBaselineSchema);
      setShowConfirmDialog(true);
    } else {
      // No existing events or no previous baseline, update directly
      updateState({ baselineSchema: newBaselineSchema });
    }
  };

  // Handle confirmation dialog actions
  const handleConfirmBaselineChange = () => {
    if (pendingBaselineSchema) {
      updateState({ baselineSchema: pendingBaselineSchema });
      showNotification('success', 'Baseline schema updated successfully');
    }
    setShowConfirmDialog(false);
    setPendingBaselineSchema(null);
  };

  const handleCancelBaselineChange = () => {
    setShowConfirmDialog(false);
    setPendingBaselineSchema(null);
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="h-full max-w-9xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-8 p-4 sm:p-6 lg:p-8 overflow-y-auto items-start"
      >
        {/* Left Column: Playbook Details Form */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 dark:border dark:border-gray-700 rounded-lg shadow-md p-10 space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Playbook Details</h2>
          </div>
          
          {/* Playbook Name */}
          <div>
            <label htmlFor="playbook-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Playbook Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="playbook-name"
              value={state.name}
              onChange={(e) => updateState({ name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
              placeholder="Enter playbook name"
              required
            />
            {!state.name && (
              <p className="mt-1 text-sm text-red-500">Playbook name is required</p>
            )}
          </div>
          
          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={state.description}
              onChange={(e) => updateState({ description: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out"
              placeholder="Enter a description for this playbook"
            />
          </div>


          {/* Schema Configuration */}
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Input Schema Column */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <LogIn04 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Input Schema
                  </label>
                </div>
                <Combobox
                  as="div"
                  className="relative"
                  value={state.input_schema_id || ''}
                  onChange={(value) => updateState({ input_schema_id: value || undefined })}
                >
                  <div className="relative w-full">
                    <div className="relative w-full cursor-default rounded-md border px-3 py-2 text-left shadow-sm focus-within:outline-none focus-within:ring-1 sm:text-sm border-gray-300 dark:border-gray-600 dark:focus-within:border-blue-600 focus-within:ring-blue-500 focus-within:border-blue-500 dark:focus-within:ring-blue-500/20 dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out">
                      <Combobox.Input
                        className="w-full bg-transparent border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400 dark:placeholder-gray-500"
                        onChange={(e) => setInputSchemaQuery(e.target.value)}
                        placeholder="Select input schema"
                        displayValue={() => selectedInputSchema?.name || ''}
                      />
                      <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" ref={inputSchemaButtonRef}>
                        <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </Combobox.Button>
                    </div>
                    
                    <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black dark:focus:bg-gray-700 ring-opacity-5 focus:outline-none sm:text-sm">
                      <Combobox.Option
                        value=""
                        className={({ active }) =>
                          `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                            active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                          }`
                        }
                      >
                        <span className="block truncate">No schema selected</span>
                      </Combobox.Option>
                      {isLoadingSchemas ? (
                        <div className="py-2 px-4 text-gray-500 dark:text-gray-400">Loading...</div>
                      ) : filteredInputSchemas.length === 0 && inputSchemaQuery !== '' ? (
                        <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No schemas found for "{inputSchemaQuery}"</div>
                      ) : (
                        filteredInputSchemas.map(schema => {
                          const isSelected = state.input_schema_id === schema.id;
                          return (
                            <Combobox.Option
                              key={schema.id}
                              value={schema.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                                }`
                              }
                            >
                              {({ active }) => (
                                <>
                                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                    {schema.name}
                                  </span>
                                  {isSelected && (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                                      }`}
                                    >
                                      <Check className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          );
                        })
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
              </div>

              {/* Output Schema Column */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <LogOut04 className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Output Schema
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Combobox
                    as="div"
                    className="relative flex-grow"
                    value={state.output_schema_id || ''}
                    onChange={(value) => updateState({ output_schema_id: value || undefined })}
                  >
                    <div className="relative w-full">
                      <div className="relative w-full cursor-default rounded-md border px-3 py-2 text-left shadow-sm focus-within:outline-none focus-within:ring-1 sm:text-sm border-gray-300 dark:border-gray-600 dark:focus-within:border-blue-600 focus-within:ring-blue-500 focus-within:border-blue-500 dark:focus-within:ring-blue-500/20 dark:bg-gray-800 text-gray-900 dark:text-gray-100 transition duration-300 ease-in-out">
                        <Combobox.Input
                          className="w-full bg-transparent border-none focus:ring-0 focus:outline-none text-sm placeholder-gray-400 dark:placeholder-gray-500"
                          onChange={(e) => setOutputSchemaQuery(e.target.value)}
                          placeholder="Select output schema"
                          displayValue={() => selectedOutputSchema?.name || ''}
                        />
                        <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2" ref={outputSchemaButtonRef}>
                          <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </Combobox.Button>
                      </div>
                      
                      <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-900 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        <Combobox.Option
                          value=""
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                              active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                            }`
                          }
                        >
                          <span className="block truncate">No schema selected</span>
                        </Combobox.Option>
                        {isLoadingSchemas ? (
                          <div className="py-2 px-4 text-gray-500 dark:text-gray-400">Loading...</div>
                      ) : filteredOutputSchemas.length === 0 && outputSchemaQuery !== '' ? (
                        <div className="py-2 px-4 text-gray-500 dark:text-gray-400">No schemas found for "{outputSchemaQuery}"</div>
                      ) : (
                        filteredOutputSchemas.map(schema => {
                          const isSelected = state.output_schema_id === schema.id;
                          return (
                            <Combobox.Option
                              key={schema.id}
                              value={schema.id}
                              className={({ active }) =>
                                `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-white'
                                }`
                              }
                            >
                              {({ active }) => (
                                <>
                                  <span className={`block truncate ${isSelected ? 'font-medium' : 'font-normal'}`}>
                                    {schema.name}
                                  </span>
                                  {isSelected && (
                                    <span
                                      className={`absolute inset-y-0 left-0 flex items-center pl-3 ${
                                        active ? 'text-white' : 'text-blue-600 dark:text-blue-400'
                                      }`}
                                    >
                                      <Check className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  )}
                                </>
                              )}
                            </Combobox.Option>
                          );
                        })
                      )}
                    </Combobox.Options>
                  </div>
                </Combobox>
                  {selectedIntegration && (
                    <img src={selectedIntegration.logo_url} alt={selectedIntegration.name} className="h-5 w-5 rounded-full" />
                  )}
                </div>
              </div>
            </div>
                {state.type === 'pipeline' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                    Input schemas describe the raw customer event data coming into the transform. Output schemas define the expected structure after the transform — your bridge between raw data and integrations. Use MetaRouter’s spec for easy Starter Kit compatibility, or set custom specs to enforce your own structure.
                  </p>
                )}
                {state.type === 'integration' && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-4">
                    Input schemas show what your transform expects to receive. Output schemas define the format sent to the vendor integration. 
                  </p>
                )}
          </div>

          {/* Advanced Settings */}
          <Disclosure as="div" className="mt-6">
            {({ open }) => (
              <>
                <Disclosure.Button className={`flex w-full items-center justify-between rounded-lg bg-gray-50 dark:bg-gray-700 px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-75 mt-10 ${open ? 'rounded-b-none' : ''}`}>
                  <span>Advanced Settings</span>
                  <ChevronDown
                    className={`${
                      open ? 'rotate-180 transform' : ''
                    } h-5 w-5 text-gray-500 dark:text-gray-400`}
                  />
                </Disclosure.Button>
                <AnimatePresence initial={false}>
                  {open && (
                    <Disclosure.Panel
                      // No 'as={motion.div}' here, it will be a regular div
                      key="content" // Keep key for AnimatePresence
                      className={`px-6 pt-3 pb-6 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-6 rounded-md space-y-6 ${open ? 'rounded-t-none' : ''}`}
                    >
                      <motion.div // This is the motion.div that will animate its content
                        initial="collapsed"
                        animate="open"
                        exit="collapsed"
                        variants={{
                          open: { opacity: 1, height: "auto" },
                          collapsed: { opacity: 0, height: 0 },
                        }}
                        transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                      >
                        {/* Event Mapping Baseline */}
                        {state.input_schema_id && state.output_schema_id && (
                          <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                              Event Mapping Baseline
                            </label>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                              Select which schema should be used as the baseline for event mappings in the editor. This determines which schema's events will be displayed.
                            </p>
                            
                            <div className="space-y-2">
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name="baselineSchema"
                                  value="input"
                                  checked={state.baselineSchema === 'input' || (!state.baselineSchema && state.type === 'pipeline')}
                                  onChange={() => handleBaselineSchemaChange('input')}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                />
                                <div className="ml-3 flex items-center gap-2">
                                  <LogIn04 className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Use Input Schema ({selectedInputSchema?.name})
                                  </span>
                                </div>
                              </label>
                              
                              <label className="flex items-center cursor-pointer">
                                <input
                                  type="radio"
                                  name="baselineSchema"
                                  value="output"
                                  checked={state.baselineSchema === 'output' || (!state.baselineSchema && state.type === 'integration')}
                                  onChange={() => handleBaselineSchemaChange('output')}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600"
                                />
                                <div className="ml-3 flex items-center gap-2">
                                  <LogOut04 className="h-4 w-4 text-green-600 dark:text-green-400" />
                                  <span className="text-sm text-gray-700 dark:text-gray-300">
                                    Use Output Schema ({selectedOutputSchema?.name})
                                  </span>
                                  {selectedIntegration && (
                                    <img src={selectedIntegration.logo_url} alt={selectedIntegration.name} className="h-4 w-4 rounded-full ml-1" />
                                  )}
                                </div>
                              </label>
                            </div>
                          </div>
                        )}

                        {/* Allow Undefined Events and Properties Toggle */}
                        <div className="space-y-3 mt-6">
                          <label className="flex items-center cursor-pointer">
                            <Switch
                              checked={state.allowUndefinedTransforms}
                              onChange={(checked) => updateState({ allowUndefinedTransforms: checked })}
                              className={`${
                                state.allowUndefinedTransforms ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                              } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                            >
                              <span
                                className={`${
                                  state.allowUndefinedTransforms ? 'translate-x-5' : 'translate-x-1'
                                } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                            <span className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                              Allow to transform events and properties not included in the baseline schema
                            </span>
                          </label>

                         {/* Transform Scope Behavior */}
                          <label className="flex items-center cursor-pointer">
                            <Switch
                              checked={state.autoPromoteGlobals}
                              onChange={(checked) => updateState({ autoPromoteGlobals: checked })}
                              className={`${
                                state.autoPromoteGlobals ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
                              } relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                            >
                              <span
                                className={`${
                                  state.autoPromoteGlobals ? 'translate-x-5' : 'translate-x-1'
                                } inline-block h-3 w-3 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                              Auto-promote all-event transforms into Global Transforms
                            </span>
                          </label>
                        </div>
                      </motion.div>
                    </Disclosure.Panel>
                  )}
                </AnimatePresence>
              </>
            )}
          </Disclosure>

        </div>

        {/* Right Column: Stats Panel */}
        <div className="lg:col-span-1 space-y-6">
          <PlaybookStatsPanel 
            playbook={state} 
            updateState={updateState} 
            mappingProgress={{
              mappedCount,
              totalCount,
              requiredMappedCount,
              requiredTotalCount,
              missingCount: 0,
              extraCount: 0,
              mismatchCount: 0,
              baselineSchema: state.baselineSchema,
            }}
          />
        </div>
      </motion.div>

      {/* Confirmation Dialog */}
      {showConfirmDialog && (
        <ConfirmDialog
          onClose={handleCancelBaselineChange}
          onConfirm={handleConfirmBaselineChange}
          title="Change Baseline Schema"
          message="Changing the baseline schema may affect existing event configurations and transforms. Are you sure you want to continue?"
          confirmText="Change Schema"
          cancelText="Cancel"
          isDanger={true}
        />
      )}
    </>
  );
};

export default PlaybookDetailsTab;
